import { useTranslation } from '@/lib/i18n';
import type { CashReceiptSearch, CashReceiptTaxType } from '../api/cashReceiptApi';
import { daysAgoKst, todayKst } from '@/features/taxInvoices/lib/taxInvoiceLib';

export { downloadCsv, formatDateTime, toCsv, todayKst, daysAgoKst, won } from '@/features/taxInvoices/lib/taxInvoiceLib';

export const KEYWORD_TYPES = ['ALL', 'ORDER_NO', 'REQUESTER', 'ISSUE_NUMBER'] as const;
export const ISSUE_STATUS_FILTER = ['ALL', 'WAITING_DEPOSIT', 'IN_PROGRESS', 'ISSUED', 'CANCELED', 'FAILED'] as const;
export const TAX_TYPE_FILTER = ['ALL', 'TAXABLE', 'FREE', 'MIXED'] as const;

/** 고도몰 기본 검색 — 신청일 기준 최근 7일. */
export const defaultCashReceiptSearch = (): CashReceiptSearch => ({
  keywordType: 'ALL',
  keyword: '',
  dateType: 'REQUESTED',
  from: daysAgoKst(7),
  to: todayKst(),
  orderStatuses: [],
  issueStatus: 'ALL',
  receiptType: 'ALL',
  identityType: 'ALL',
  taxType: 'ALL',
  amountChangedOnly: false,
  minAmount: '',
  maxAmount: '',
});

export interface AmountSplit {
  supplyAmount: number;
  vatAmount: number;
  taxFreeAmount: number;
}

/**
 * 발행액 → 공급액·부가세·면세(고도몰 개별발급의 자동 계산). 과세분은 부가세 포함가로 보고 공급액 = round(과세분 / 1.1).
 * 복합과세는 운영자가 넣은 면세액을 빼고 나머지를 과세분으로 나눈다(면세액이 발행액보다 크면 발행액으로 자른다).
 */
export const splitAmount = (amount: number, taxType: CashReceiptTaxType, taxFreeInput = 0): AmountSplit => {
  const total = Math.max(0, Math.floor(amount || 0));
  if (taxType === 'FREE') return { supplyAmount: 0, vatAmount: 0, taxFreeAmount: total };
  const free = taxType === 'MIXED' ? Math.min(total, Math.max(0, Math.floor(taxFreeInput || 0))) : 0;
  const taxable = total - free;
  const supply = Math.round(taxable / 1.1);
  return { supplyAmount: supply, vatAmount: taxable - supply, taxFreeAmount: free };
};

/** 휴대폰번호 10~11자리(01X). */
export const isValidPhone = (v: string): boolean => /^01\d{8,9}$/.test(v.replace(/\D/g, ''));
/** 사업자등록번호 10자리. */
export const isValidBusinessNo = (v: string): boolean => /^\d{10}$/.test(v.replace(/\D/g, ''));
export const isValidEmail = (v: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

export const useCashReceiptLabels = () => {
  const { t } = useTranslation('orders');
  return {
    t,
    keywordType: (k: string) =>
      ({
        ALL: t('orders.cashReceiptAdmin.keywordType.ALL', '통합검색'),
        ORDER_NO: t('orders.cashReceiptAdmin.keywordType.ORDER_NO', '주문번호'),
        REQUESTER: t('orders.cashReceiptAdmin.keywordType.REQUESTER', '신청자'),
        ISSUE_NUMBER: t('orders.cashReceiptAdmin.keywordType.ISSUE_NUMBER', '거래번호'),
      })[k] ?? k,
    issueStatus: (k: string) =>
      ({
        ALL: t('orders.cashReceiptAdmin.all', '전체'),
        WAITING_DEPOSIT: t('orders.cashReceipt.status.WAITING_DEPOSIT', '입금대기'),
        REQUESTED: t('orders.cashReceipt.status.IN_PROGRESS', '발급중'),
        IN_PROGRESS: t('orders.cashReceipt.status.IN_PROGRESS', '발급중'),
        ISSUED: t('orders.cashReceipt.status.ISSUED', '발급완료'),
        CANCELED: t('orders.cashReceipt.status.CANCELED', '취소'),
        FAILED: t('orders.cashReceipt.status.FAILED', '실패'),
      })[k] ?? k,
    receiptType: (k: string) =>
      ({
        ALL: t('orders.cashReceiptAdmin.all', '전체'),
        INCOME_DEDUCTION: t('orders.cashReceipt.type.INCOME_DEDUCTION', '소득공제'),
        EXPENSE_PROOF: t('orders.cashReceipt.type.EXPENSE_PROOF', '지출증빙'),
      })[k] ?? k,
    identityType: (k: string) =>
      ({
        ALL: t('orders.cashReceiptAdmin.all', '전체'),
        PHONE: t('orders.cashReceipt.identity.PHONE', '휴대폰번호'),
        BUSINESS_NO: t('orders.cashReceipt.identity.BUSINESS_NO', '사업자등록번호'),
      })[k] ?? k,
    taxType: (k: string) =>
      ({
        ALL: t('orders.cashReceiptAdmin.all', '전체'),
        TAXABLE: t('orders.taxInvoice.taxType.TAXABLE', '과세'),
        FREE: t('orders.taxInvoice.taxType.FREE', '면세'),
        MIXED: t('orders.taxInvoice.taxType.MIXED', '복합과세'),
      })[k] ?? k,
    receiptSource: (k: string | null) =>
      k
        ? ({
            API: t('orders.cashReceipt.receiptSource.API', '쇼핑몰 발급'),
            PG_WINDOW: t('orders.cashReceipt.receiptSource.PG_WINDOW', '결제창(Toss)'),
            LEGACY: t('orders.cashReceipt.receiptSource.LEGACY', '고도몰 이관'),
          })[k] ?? k
        : '',
    paymentMethod: (k: string | null) => (k ? t(`orders.paymentMethod.${k}`, k) : '-'),
    orderStatus: (k: string | null) => (k ? t(`orders.orderStatus.${k}`, k) : '-'),
  };
};
